/**
 * Adapted from PlayCanvas Engine's gsplat-data.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'

export type PlyStorage =
  | Float32Array
  | Float64Array
  | Int8Array
  | Uint8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array

type PlyStorageConstructor<T extends PlyStorage> = new (buffer: ArrayBufferLike) => T

/**
 * PLY属性定义
 */
export interface PlyProperty {
  /** 属性名，如 x/y/z/rot_0/f_dc_0 */
  name: string
  /** 属性类型，常见值 float/uchar/int */
  type: string
  /** 存储缓冲，null 表示被过滤跳过的属性 */
  storage: PlyStorage | null
  /** PLY property 的单元素字节数；旧 Loader 可选填写。 */
  byteSize?: number
}

/**
 * PLY元素定义（通常是 vertex 元素）
 */
export interface PlyElement {
  /** 元素名称，通常为 'vertex' */
  name: string
  /** 元素条目数，通常等于 splat 数量 */
  count: number
  /** 当前元素的属性列表 */
  properties: PlyProperty[]
}

/** PlayCanvas immediate wire-bounds 的最小宿主接口。 */
export interface SplatWireframeScene {
  readonly immediate: {
    drawWireAlignedBox(
      min: THREE.Vector3,
      max: THREE.Vector3,
      color: Readonly<{ r: number; g: number; b: number; a: number }>,
      depthTest: boolean,
      layer: unknown,
      matrix: THREE.Matrix4
    ): void
  }
  readonly defaultDrawLayer: unknown
}

/**
 * Splat 迭代器 - 用于访问未压缩的 splat 数据
 * 自动处理旋转、缩放、颜色的解码
 */
class SplatIterator {
  read: (i: number) => void

  constructor(
    gsplatData: GSplatData,
    p?: THREE.Vector3,
    r?: THREE.Quaternion,
    s?: THREE.Vector3,
    c?: THREE.Vector4
  ) {
    const activated = gsplatData.activated
    const x = gsplatData.getProp('x') as Float32Array
    const y = gsplatData.getProp('y') as Float32Array
    const z = gsplatData.getProp('z') as Float32Array

    const rx = gsplatData.getProp('rot_1') as Float32Array
    const ry = gsplatData.getProp('rot_2') as Float32Array
    const rz = gsplatData.getProp('rot_3') as Float32Array
    const rw = gsplatData.getProp('rot_0') as Float32Array

    const sx = gsplatData.getProp('scale_0') as Float32Array
    const sy = gsplatData.getProp('scale_1') as Float32Array
    const sz = gsplatData.getProp('scale_2') as Float32Array

    const cr = gsplatData.getProp('f_dc_0') as Float32Array
    const cg = gsplatData.getProp('f_dc_1') as Float32Array
    const cb = gsplatData.getProp('f_dc_2') as Float32Array
    const ca = gsplatData.getProp('opacity') as Float32Array

    this.read = (i: number) => {
      if (p) {
        p.x = x![i]
        p.y = y![i]
        p.z = z![i]
      }

      if (r) {
        r.set(rx![i], ry![i], rz![i], rw![i])
      }

      if (s) {
        if (activated) s.set(sx![i], sy![i], sz![i])
        else s.set(Math.exp(sx![i]), Math.exp(sy![i]), Math.exp(sz![i]))
      }

      if (c) {
        c.set(
          0.5 + cr![i] * SH_C0,
          0.5 + cg![i] * SH_C0,
          0.5 + cb![i] * SH_C0,
          activated ? ca![i] : sigmoid(ca![i])
        )
      }
    }
  }
}

/**
 * Splat数据容器
 * 存储从PLY文件解析出的原始数据
 */
export class GSplatData {
  /** 所有 PLY 元素，位置 0 通常为 vertex 元素 */
  elements: PlyElement[]
  /** splat 数量，等于第一个元素的 count */
  numSplats: number

  /** scale/opacity 是否已分别执行 exp/sigmoid 激活。 */
  activated = false

  /**
   * 文件头注释（从 PLY 文件头提取）
   * @type {string[]}
   */
  comments: string[]

  /**
   * 构造函数
   * @param elements PLY 元素数组（可选）
   * @param comments 文件头注释（可选）
   */
  constructor(elements?: PlyElement[], comments: string[] = []) {
    this.elements = elements || []
    this.numSplats = this.elements.length > 0 ? this.elements[0].count : 0
    this.comments = comments
  }

  /** 按名称访问 PLY element；与冻结 PlayCanvas GSplatData 保持一致。 */
  getElement(name: string): PlyElement | undefined {
    return this.elements.find((element) => element.name === name)
  }

  /**
   * 获取指定名称的属性数组
   * @param name 属性名称 (如 'x', 'y', 'z', 'rot_0')
   * @returns 属性数据数组，未找到则返回 null
   */
  getProp<T = Float32Array>(name: string, elementName = 'vertex'): T | null {
    return (this.getElement(elementName)?.properties.find((property) => property.name === name)
      ?.storage ?? null) as T | null
  }

  /** 向 vertex element 追加 float property。 */
  addProp(name: string, storage: Float32Array): void {
    const vertex = this.getElement('vertex')
    if (!vertex) throw new Error('SplatData.addProp: 缺少 vertex element')
    if (storage.length !== vertex.count) {
      throw new Error(
        `SplatData.addProp: property "${name}" 长度不匹配，expected=${vertex.count}, actual=${storage.length}`
      )
    }
    vertex.properties.push({ type: 'float', name, storage, byteSize: 4 })
  }

  /**
   * 创建 splat 迭代器
   * @param p 接收位置的 Vector3（可选）
   * @param r 接收旋转的 Quaternion（可选）
   * @param s 接收缩放的 Vector3（可选）
   * @param c 接收颜色的 Vector4（可选）
   * @returns 迭代器实例
   */
  createIter(
    p?: THREE.Vector3,
    r?: THREE.Quaternion,
    s?: THREE.Vector3,
    c?: THREE.Vector4
  ): SplatIterator {
    return new SplatIterator(this, p, r, s, c)
  }

  /**
   * 获取中心点数组（用于排序）。
   *
   * @returns 长度为 numSplats*3 的 Float32Array，格式 [x0,y0,z0,x1,y1,z1,...]
   */
  getCenters(): Float32Array {
    const x = this.getProp('x') as Float32Array
    const y = this.getProp('y') as Float32Array
    const z = this.getProp('z') as Float32Array

    if (!x || !y || !z) {
      throw new Error('Missing position properties (x, y, z)')
    }

    const centers = new Float32Array(this.numSplats * 3)
    for (let i = 0; i < this.numSplats; i++) {
      centers[i * 3 + 0] = x[i]
      centers[i * 3 + 1] = y[i]
      centers[i * 3 + 2] = z[i]
    }

    return centers
  }

  /**
   * 计算包围盒（带缓存优化）
   * @param result 输出的包围盒对象
   * @returns 是否成功计算
   */
  calcAabb(result: THREE.Box3, pred?: (i: number) => boolean): boolean {
    const x = this.getProp('x') as Float32Array
    const y = this.getProp('y') as Float32Array
    const z = this.getProp('z') as Float32Array
    const sx = this.getProp('scale_0') as Float32Array
    const sy = this.getProp('scale_1') as Float32Array
    const sz = this.getProp('scale_2') as Float32Array

    let minX = 0
    let minY = 0
    let minZ = 0
    let maxX = 0
    let maxY = 0
    let maxZ = 0
    let first = true

    for (let i = 0; i < this.numSplats; i++) {
      if (pred && !pred(i)) continue
      const px = x[i]
      const py = y[i]
      const pz = z[i]
      const scale = Math.max(sx[i], sy[i], sz[i])
      if (
        !Number.isFinite(px) ||
        !Number.isFinite(py) ||
        !Number.isFinite(pz) ||
        !Number.isFinite(scale)
      ) {
        continue
      }
      const radius = 2 * (this.activated ? scale : Math.exp(scale))
      if (first) {
        first = false
        minX = px - radius
        minY = py - radius
        minZ = pz - radius
        maxX = px + radius
        maxY = py + radius
        maxZ = pz + radius
      } else {
        minX = Math.min(minX, px - radius)
        minY = Math.min(minY, py - radius)
        minZ = Math.min(minZ, pz - radius)
        maxX = Math.max(maxX, px + radius)
        maxY = Math.max(maxY, py + radius)
        maxZ = Math.max(maxZ, pz + radius)
      }
    }
    if (first) return false
    result.min.set(minX, minY, minZ)
    result.max.set(maxX, maxY, maxZ)
    return true
  }

  /**
   * 清除 AABB 缓存（数据变更时调用）
   */
  invalidateAabbCache(): void {
    // PlayCanvas 对齐实现按调用即时计算；保留旧 API 作为严格 no-op。
  }

  /**
   * 计算精确包围盒（考虑旋转和缩放）
   * @param result 输出的包围盒对象
   * @param pred 可选的过滤谓词函数
   * @returns 是否成功计算
   */
  calcAabbExact(result: THREE.Box3, pred?: (i: number) => boolean): boolean {
    const p = new THREE.Vector3()
    const r = new THREE.Quaternion()
    const s = new THREE.Vector3()

    const iter = this.createIter(p, r, s)
    const tempBox = new THREE.Box3()

    let first = true

    for (let i = 0; i < this.numSplats; i++) {
      if (pred && !pred(i)) {
        continue
      }

      iter.read(i)

      if (first) {
        first = false
        GSplatData.calcSplatAabb(result, p, r, s)
      } else {
        GSplatData.calcSplatAabb(tempBox, p, r, s)
        result.union(tempBox)
      }
    }

    return !first
  }

  /**
   * 计算加权焦点（权重 = 1 / (1 + exp(max_scale))）
   * @param result 输出的焦点位置
   * @param pred 可选的过滤谓词函数
   */
  calcFocalPoint(result: THREE.Vector3, pred?: (i: number) => boolean): void {
    const x = this.getProp('x') as Float32Array
    const y = this.getProp('y') as Float32Array
    const z = this.getProp('z') as Float32Array
    const sx = this.getProp('scale_0') as Float32Array
    const sy = this.getProp('scale_1') as Float32Array
    const sz = this.getProp('scale_2') as Float32Array

    result.set(0, 0, 0)

    let sum = 0
    for (let i = 0; i < this.numSplats; i++) {
      if (pred && !pred(i)) {
        continue
      }

      const px = x[i]
      const py = y[i]
      const pz = z[i]

      if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) {
        continue
      }

      const maxScale = Math.max(sx[i], sy[i], sz[i])
      const weight = 1 / (1 + (this.activated ? maxScale : Math.exp(maxScale)))
      result.x += px * weight
      result.y += py * weight
      result.z += pz * weight
      sum += weight
    }

    result.multiplyScalar(1 / sum)
  }

  /** 按冻结上游顺序为每个 splat 提交局部 ±2*scale 的 wire bounds。 */
  renderWireframeBounds(scene: SplatWireframeScene, worldMatrix: THREE.Matrix4): void {
    const position = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    const min = new THREE.Vector3()
    const max = new THREE.Vector3()
    const localMatrix = new THREE.Matrix4()
    const drawMatrix = new THREE.Matrix4()
    const iter = this.createIter(position, rotation, scale)

    for (let index = 0; index < this.numSplats; index++) {
      iter.read(index)
      localMatrix.compose(position, rotation.normalize(), UNIT_SCALE)
      drawMatrix.multiplyMatrices(worldMatrix, localMatrix)
      min.set(scale.x * -2, scale.y * -2, scale.z * -2)
      max.set(scale.x * 2, scale.y * 2, scale.z * 2)
      scene.immediate.drawWireAlignedBox(
        min,
        max,
        DEBUG_BOUNDS_COLOR,
        true,
        scene.defaultDrawLayer,
        drawMatrix
      )
    }
  }

  /**
   * 按索引数组重排所有属性
   * @param order 排序索引数组
   */
  reorder(order: Uint32Array): void {
    this.validatePermutation(order)
    const staged: Array<{ property: PlyProperty; storage: PlyStorage }> = []
    for (const element of this.elements) {
      for (const property of element.properties) {
        const source = property.storage
        if (!source) continue
        if (!isPlyStorage(source)) {
          throw new Error(`SplatData.reorder: property "${property.name}" 使用不支持的 storage`)
        }
        if (source.length !== this.numSplats) {
          throw new Error(
            `SplatData.reorder: property "${property.name}" 长度不匹配，expected=${this.numSplats}, actual=${source.length}`
          )
        }
      }
    }

    for (const element of this.elements) {
      for (const property of element.properties) {
        const source = property.storage
        if (!source) continue
        const ctor = source.constructor as PlyStorageConstructor<typeof source>
        const result = new ctor(new ArrayBuffer(source.byteLength))
        for (let index = 0; index < order.length; index++) result[index] = source[order[index]]
        staged.push({ property, storage: result })
      }
    }

    for (const { property, storage } of staged) property.storage = storage
  }

  /**
   * 使用 Morton 码重排数据（提高空间局部性）
   */
  reorderData(): void {
    this.reorder(this.calcMortonOrder())
  }

  /**
   * 计算单个 splat 的精确包围盒（考虑旋转）
   * @param result 输出的包围盒
   * @param p 位置
   * @param r 旋转
   * @param s 缩放
   */
  static calcSplatAabb(
    result: THREE.Box3,
    p: THREE.Vector3,
    r: THREE.Quaternion,
    s: THREE.Vector3
  ): void {
    const normalizedRotation = r.clone().normalize()
    const mat = new THREE.Matrix4()
    mat.compose(p, normalizedRotation, new THREE.Vector3(1, 1, 1))

    // 创建局部包围盒（未旋转的椭球体近似为盒子）
    const localBox = new THREE.Box3()
    localBox.min.set(-s.x * 2, -s.y * 2, -s.z * 2)
    localBox.max.set(s.x * 2, s.y * 2, s.z * 2)

    // 应用变换
    result.makeEmpty()
    const corners = [
      new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.min.z),
      new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.min.z),
      new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.min.z),
      new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.min.z),
      new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.max.z),
      new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.max.z),
      new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.max.z),
      new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.max.z),
    ]

    for (const corner of corners) {
      corner.applyMatrix4(mat)
      result.expandByPoint(corner)
    }
  }

  /**
   * 检测球谐系数阶数
   * 参考 PlayCanvas gsplat-data.js 的 shBands 实现
   * @returns 0 (无SH), 1 (1阶, 3个系数), 2 (2阶, 8个系数), 3 (3阶, 15个系数)
   */
  get shBands(): number {
    // 检测 f_rest_xx 属性的数量
    // SH bands 映射：
    // 0: 只有 DC (f_dc_0, f_dc_1, f_dc_2)
    // 1: DC + 9 个 rest (f_rest_0 ~ f_rest_8)    -> 共 3 个 SH 系数
    // 2: DC + 24 个 rest (f_rest_0 ~ f_rest_23)  -> 共 8 个 SH 系数
    // 3: DC + 45 个 rest (f_rest_0 ~ f_rest_44)  -> 共 15 个 SH 系数

    // 统计 f_rest_ 属性数量
    let numRestProps = 0
    for (let i = 0; i < 45; i++) {
      if (!this.getProp(`f_rest_${i}`)) {
        break
      }
      numRestProps++
    }

    // 根据属性数量映射到 bands
    // PlayCanvas 的映射表：{9: 1, 24: 2, 45: 3}
    const bandMap: Record<number, number> = {
      9: 1,
      24: 2,
      45: 3,
    }

    return bandMap[numRestProps] ?? 0
  }

  /**
   * 计算Morton码顺序
   * 使用Z-order空间填充曲线对splat进行排序，提高空间局部性
   * 使用 Map 处理 Morton 码碰撞，确保排序稳定性
   * @returns 排序后的索引数组
   */
  calcMortonOrder(): Uint32Array {
    const x = this.getProp('x') as Float32Array
    const y = this.getProp('y') as Float32Array
    const z = this.getProp('z') as Float32Array

    if (!x || !y || !z || this.numSplats === 0) {
      return new Uint32Array(0)
    }

    const { min: minX, max: maxX } = calcMinMax(x)
    const { min: minY, max: maxY } = calcMinMax(y)
    const { min: minZ, max: maxZ } = calcMinMax(z)
    const sizeX = minX === maxX ? 0 : 1024 / (maxX - minX)
    const sizeY = minY === maxY ? 0 : 1024 / (maxY - minY)
    const sizeZ = minZ === maxZ ? 0 : 1024 / (maxZ - minZ)

    // 使用 Map 收集相同 Morton 码的索引，确保排序稳定性
    // 当多个 splat 映射到相同的 Morton 码时，保持原始顺序
    const codes = new Map<number, number[]>()

    for (let i = 0; i < this.numSplats; i++) {
      // 归一化到 [0, 1023] 范围（10 bits per dimension = 30 bits total）
      const ix = Math.min(1023, Math.floor((x[i] - minX) * sizeX))
      const iy = Math.min(1023, Math.floor((y[i] - minY) * sizeY))
      const iz = Math.min(1023, Math.floor((z[i] - minZ) * sizeZ))

      // 计算 Morton 码
      const code = this.encodeMorton3D(ix, iy, iz)

      // 收集相同码值的索引
      const existing = codes.get(code)
      if (existing) {
        existing.push(i)
      } else {
        codes.set(code, [i])
      }
    }

    // 按 Morton 码排序
    const sortedKeys = Array.from(codes.keys()).sort((a, b) => a - b)

    // 构建结果数组
    const result = new Uint32Array(this.numSplats)
    let idx = 0
    for (const key of sortedKeys) {
      const indices = codes.get(key)!
      for (const i of indices) {
        result[idx++] = i
      }
    }

    return result
  }

  /**
   * 编码3D Morton码（Z-order curve）
   * 将3D坐标交织编码成单个整数
   * @param x X坐标（0-1023）
   * @param y Y坐标（0-1023）
   * @param z Z坐标（0-1023）
   * @returns 30-bit Morton码
   */
  private encodeMorton3D(x: number, y: number, z: number): number {
    const part1By2 = (value: number): number => {
      value &= 0x000003ff
      value = (value ^ (value << 16)) & 0xff0000ff
      value = (value ^ (value << 8)) & 0x0300f00f
      value = (value ^ (value << 4)) & 0x030c30c3
      value = (value ^ (value << 2)) & 0x09249249
      return value
    }
    return (part1By2(z) << 2) + (part1By2(y) << 1) + part1By2(x)
  }

  private validatePermutation(order: Uint32Array): void {
    if (!(order instanceof Uint32Array) || order.length !== this.numSplats) {
      throw new Error(
        `SplatData.reorder: order 长度不匹配，expected=${this.numSplats}, actual=${order.length}`
      )
    }
    const seen = new Uint8Array(this.numSplats)
    for (let index = 0; index < order.length; index++) {
      const sourceIndex = order[index]
      if (sourceIndex >= this.numSplats || seen[sourceIndex] !== 0) {
        throw new Error(
          `SplatData.reorder: order 不是 0..${Math.max(0, this.numSplats - 1)} 的全排列，index=${index}, value=${sourceIndex}`
        )
      }
      seen[sourceIndex] = 1
    }
  }

  /**
   * 是否为压缩格式
   * @returns 总是返回 false（未压缩）
   */
  get isCompressed(): boolean {
    return false
  }

  /**
   * 释放资源
   */
  dispose(): void {
    this.elements = []
    this.numSplats = 0
  }
}

const SH_C0 = 0.28209479177387814
const UNIT_SCALE = Object.freeze(new THREE.Vector3(1, 1, 1))
const DEBUG_BOUNDS_COLOR = Object.freeze({ r: 1, g: 1, b: 0, a: 0.4 })

function sigmoid(value: number): number {
  if (value > 0) return 1 / (1 + Math.exp(-value))
  const exponential = Math.exp(value)
  return exponential / (1 + exponential)
}

function calcMinMax(values: Float32Array): { min: number; max: number } {
  let min = values[0]
  let max = values[0]
  for (let index = 1; index < values.length; index++) {
    if (values[index] < min) min = values[index]
    if (values[index] > max) max = values[index]
  }
  return { min, max }
}

function isPlyStorage(value: unknown): value is PlyStorage {
  return (
    value instanceof Float32Array ||
    value instanceof Float64Array ||
    value instanceof Int8Array ||
    value instanceof Uint8Array ||
    value instanceof Uint8ClampedArray ||
    value instanceof Int16Array ||
    value instanceof Uint16Array ||
    value instanceof Int32Array ||
    value instanceof Uint32Array
  )
}
